/* eslint-disable */
import type { Image } from "../../ui/Image.pb";
import type { ExternalAppLink } from "./ExternalAppLink.pb";
import type { Identifier } from "./Identifier.pb";
import type { ImageAsset } from "./Image.pb";

export enum Status {
  /** UNKNOWN_STATUS - set a default value */
  UNKNOWN_STATUS = "UNKNOWN_STATUS",
  ACTIVE = "ACTIVE",
  /** DELETED - deleted status will not be shown in search results */
  DELETED = "DELETED",
}

export enum Feature {
  UNKNOWN_FEATURE = "UNKNOWN_FEATURE",
  CASH_APP_PAY_LATER = "CASH_APP_PAY_LATER",
  AFTERPAY_APP = "AFTERPAY_APP",
  CASH_APP_PAY_OFFER = "CASH_APP_PAY_OFFER",
  CASH_CARD_BOOST = "CASH_CARD_BOOST",
  SQ_LOYALTY_PROGRAM = "SQ_LOYALTY_PROGRAM",
  SQ_COUPON = "SQ_COUPON",
  AFTERPAY_APP_SUP = "AFTERPAY_APP_SUP",
  CASH_APP_SUP = "CASH_APP_SUP",
  AFTERPAY_APP_IN_STORE = "AFTERPAY_APP_IN_STORE",
  AFTERPAY_APP_GIFT_CARD = "AFTERPAY_APP_GIFT_CARD",
}

export enum BusinessType {
  UNKNOWN_BUSINESS_TYPE = "UNKNOWN_BUSINESS_TYPE",
  AP_STORE = "AP_STORE",
  BOOST_MERCHANT = "BOOST_MERCHANT",
  CAP_MERCHANT = "CAP_MERCHANT",
  SQ_LOYALTY_MERCHANT = "SQ_LOYALTY_MERCHANT",
  SQ_OFFER_MERCHANT = "SQ_OFFER_MERCHANT",
}

export enum TargetApp {
  TARGET_CASH_APP = "TARGET_CASH_APP",
  TARGET_AFTERPAY = "TARGET_AFTERPAY",
}

export interface GetBusinessProfileRequest {
  /**
   * Business Profile Tokens, starting with BP_
   * Only one of the ID fields should be set (tokens, afterpay_store_ids, merchantein_parent_tokens, identifiers)
   */
  tokens?: string[];
  /**
   * Afterpay Store IDs.
   * Only one of the ID fields should be set (tokens, afterpay_store_ids, merchantein_parent_tokens, identifiers)
   */
  afterpay_store_ids?: string[];
  /** Data fields returned in response, will return all data if left null or empty list */
  included_profile_fields?: GetBusinessProfileRequestProfileField[];
  /**
   * Merchantein parent tokens.
   * Only one of the ID fields should be set (tokens, afterpay_store_ids, merchantein_parent_tokens, identifiers)
   */
  merchantein_parent_tokens?: string[];
  /**
   * Identifier fields.
   * Only one of the ID fields should be set (tokens, afterpay_store_ids, merchantein_parent_tokens, identifiers)
   */
  identifiers?: Identifier[];
}

export enum GetBusinessProfileRequestProfileField {
  UNKNOWN_FIELD = "UNKNOWN_FIELD",
  BUSINESS_STATUSES = "BUSINESS_STATUSES",
  FEATURES = "FEATURES",
  BUSINESS_METADATA = "BUSINESS_METADATA",
  IMAGE_ASSETS = "IMAGE_ASSETS",
}

export interface GetBusinessProfileResponse {
  business_profiles?: BusinessProfile[];
}

export interface EditBusinessProfileRequest {
  /** Merchantein parent token for the business profile to be edited. */
  merchantein_parent_token?: string;
  /**
   * Business profile fields to merge in to the existing business profile record.
   * Currently, only the following fields are editable - any others will be ignored:
   * - business_profile.image_assets - images of the same type will be replaced, others retained
   * - business_profile.business_metadata.cash_app_generic_business_metadata - fields provided will be replaced, and can be reset by providing an empty string
   */
  business_profile?: BusinessProfile;
}

export interface EditBusinessProfileResponse {
  business_profile?: BusinessProfile;
}

export interface BusinessStatus {
  business_type?: BusinessType;
  is_active?: boolean;
}

export interface AfterpayAppMetadata {
  name?: string;
  shop_url?: string;
  merchant_id?: string;
  main_image_url?: string;
  logo_url?: string;
  in_store_only?: boolean;
  new_logo_url?: string;
  last_outbound_count?: number;
  merchant_outbound_url?: string;
  avatar_url?: string;
  store_short_name?: string;
  background_color?: string;
}

export interface CashAppPayLaterMetadata {
  affiliate_url?: string;
  lifestyle_images?: ImageAsset[];
  /** @deprecated */
  cash_pay_brand_bizzy_base_token?: string;
  cash_app_pay_brand_id?: string;
}

export interface CashAppGenericBusinessMetadata {
  /**
   * A list of regular expressions, used by the IAB to test URLs against, to determine when
   * checkout has been completed successfully.
   */
  post_checkout_url_patterns?: string[];
  /**
   * Enables linking directly to the business's own mobile app, or the app download page
   * on the relevant platform's app store.
   */
  external_app_link?: ExternalAppLink;
}

export interface BusinessMetadata {
  cash_app_pay_later_metadata?: CashAppPayLaterMetadata;
  cash_app_sup_metadata?: CashAppPayLaterMetadata;
  afterpay_app_metadata?: AfterpayAppMetadata;
  /** @deprecated */
  afterpay_app_sup_metadata?: AfterpayAppMetadata;
  /** Business-level metadata we wish to capture independent of the source / vertical. */
  cash_app_generic_business_metadata?: CashAppGenericBusinessMetadata;
}

export interface BusinessProfile {
  /** token starts with BP_ */
  token?: string;
  name?: string;
  status?: Status;
  logo?: Image;
  /** The searchable tags attached to the business. A typical usage is to assign businesses to the UI modules. */
  tags?: string[];
  canonical_category?: string;
  categories?: string[];
  image_assets?: ImageAsset[];
  features?: Feature[];
  website_url?: string;
  business_metadata?: BusinessMetadata;
  /** This is for internal use only, will not be used for ES filtering */
  target_app?: TargetApp;
  region?: string;
  /** This field is specific for Afterpay use and should not be populated for Cash App data */
  locale?: string;
  version?: number;
  /** This field is used to assess when this business was created in AP */
  external_created_at?: number;
  /** Afterpay store id */
  afterpay_store_id?: string;
  /** Merchantein parent token */
  merchantein_token?: string;
  /** Canonical bizzy token */
  bizzy_token?: string;
  /** Bizzy Brand ID */
  bizzy_brand_id?: string;
  /** Canonical Afterpay API+ merchant token */
  afterpay_merchant_id?: string;
  /** @deprecated */
  affiliate_link?: string;
  /** @deprecated */
  business_statuses?: BusinessStatus[];
}
